// frontend/src/components/admin/SystemUpdate.js
// Dev-only "System Update" panel (Settings -> System Update tab).
//
// Talks to the dev-gated /api/update/* routes. Progress is a POLLED status
// file surfaced by the backend: during an update the backend container itself
// is recreated and the host channel restarts, so polling MUST tolerate
// transient failures — we keep showing the last status with a "reconnecting…"
// notice instead of erroring out.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';

const ACTIVE_STATES = ['starting', 'running', 'restoring'];
const ACTIVE_POLL_MS = 2000;
const IDLE_POLL_MS = 30000;

const cmpSemver = (a, b) => {
  const pa = String(a || '').replace(/^v/, '').split('.').map(Number);
  const pb = String(b || '').replace(/^v/, '').split('.').map(Number);
  for (let i = 0; i < 3; i += 1) {
    if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) - (pb[i] || 0);
  }
  return 0;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const StepIcon = ({ status }) => {
  switch (status) {
    case 'done':
      return <CheckCircleIcon color="success" fontSize="small" />;
    case 'running':
      return <SyncIcon color="primary" fontSize="small" sx={{ animation: 'spin 1.5s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />;
    case 'warn':
      return <WarningAmberIcon color="warning" fontSize="small" />;
    case 'failed':
      return <ErrorOutlineIcon color="error" fontSize="small" />;
    case 'skipped':
      return <RemoveCircleOutlineIcon color="disabled" fontSize="small" />;
    default:
      return <RadioButtonUncheckedIcon color="disabled" fontSize="small" />;
  }
};

const SystemUpdate = () => {
  const [components, setComponents] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionsError, setVersionsError] = useState('');
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selectedVersion, setSelectedVersion] = useState('');
  const [includeImages, setIncludeImages] = useState(true);
  const [includeHostFiles, setIncludeHostFiles] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [snack, setSnack] = useState(null); // {severity, text}

  const [uiVersion, setUiVersion] = useState(null);
  const [reloadNeeded, setReloadNeeded] = useState(false);

  const wasActiveRef = useRef(false);
  const initialUiVersionRef = useRef(null);

  const isActive = !!status && ACTIVE_STATES.includes(status.state);

  // version.json is served with no-cache and fetched with no-store so a fresh
  // browser load right after an update reports the NEW build immediately.
  const fetchUiVersion = useCallback(async () => {
    try {
      const resp = await fetch('/version.json', { cache: 'no-store' });
      if (!resp.ok) return null;
      const data = await resp.json();
      return data.version || null;
    } catch (e) {
      return null;
    }
  }, []);

  const loadStatic = useCallback(async () => {
    const results = await Promise.allSettled([
      api.get('/update/components', { skipErrorAlert: true }),
      api.get('/update/available', { skipErrorAlert: true }),
      api.get('/update/history', { skipErrorAlert: true }),
    ]);
    if (results[0].status === 'fulfilled') setComponents(results[0].value.data);
    if (results[1].status === 'fulfilled') {
      setVersions(results[1].value.data.versions || []);
      setVersionsError('');
    } else {
      setVersionsError(
        results[1].reason?.response?.data?.detail ||
          results[1].reason?.response?.data?.error ||
          'Could not reach the image registry'
      );
    }
    if (results[2].status === 'fulfilled') setHistory(results[2].value.data.history || []);
  }, []);

  const pollStatus = useCallback(async () => {
    try {
      const resp = await api.get('/update/status', { skipErrorAlert: true });
      setStatus(resp.data.status);
      setReconnecting(false);
    } catch (e) {
      // Expected mid-update: the backend container is being recreated.
      setReconnecting(true);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([loadStatic(), pollStatus()]);
      const v = await fetchUiVersion();
      setUiVersion(v);
      initialUiVersionRef.current = v;
      setLoading(false);
    })();
  }, [loadStatic, pollStatus, fetchUiVersion]);

  // Adaptive polling: fast while a run is active (or we lost the backend
  // mid-run), slow when idle.
  useEffect(() => {
    const fast = isActive || (reconnecting && wasActiveRef.current);
    const id = setInterval(pollStatus, fast ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    return () => clearInterval(id);
  }, [isActive, reconnecting, pollStatus]);

  // Detect run completion -> toast, refresh everything, check for a new UI build.
  useEffect(() => {
    if (isActive) {
      wasActiveRef.current = true;
      return;
    }
    if (!wasActiveRef.current || !status) return;
    wasActiveRef.current = false;
    if (status.state === 'complete') {
      setSnack({ severity: 'success', text: `Update to ${status.version} completed` });
    } else if (status.state === 'failed') {
      setSnack({
        severity: status.restored ? 'warning' : 'error',
        text: status.restored
          ? `Update to ${status.version} failed — automatically restored the previous version`
          : `Update to ${status.version} failed`,
      });
    }
    loadStatic();
    fetchUiVersion().then((v) => {
      setUiVersion(v);
      if (v && initialUiVersionRef.current && v !== initialUiVersionRef.current) {
        setReloadNeeded(true);
      }
    });
  }, [isActive, status, loadStatic, fetchUiVersion]);

  const currentVersion =
    components?.app || versions.find((v) => v.isCurrent)?.version || null;
  const selectable = versions.filter((v) => !v.isCurrent);
  const selected = versions.find((v) => v.version === selectedVersion) || null;
  const isRollback =
    !!selected && !!currentVersion && cmpSemver(selected.version, currentVersion) < 0;
  const agentOnline = !!components?.hostAgent?.connected;

  const startRun = async () => {
    setConfirmOpen(false);
    setStarting(true);
    try {
      const endpoint = isRollback ? '/update/rollback' : '/update/start';
      await api.post(
        endpoint,
        { version: selectedVersion, includeImages, includeHostFiles },
        { skipErrorAlert: true }
      );
      setSnack({
        severity: 'info',
        text: `${isRollback ? 'Rollback' : 'Update'} to ${selectedVersion} started`,
      });
      wasActiveRef.current = true;
      await pollStatus();
    } catch (e) {
      setSnack({
        severity: 'error',
        text: e.response?.data?.error || 'Failed to start the update',
      });
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const comp = components?.components || {};
  const latestHistory = history[0];
  const showRestoreBanner =
    !isActive &&
    ((status && status.state === 'failed' && status.restored) ||
      (!status && latestHistory?.restored));

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        System Update
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Updates the application docker images and the host-side scripts from a
        published release. If anything fails, the device restores itself to the
        current working version automatically.
      </Typography>

      {reloadNeeded && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        >
          A new version of this interface is installed — reload to use it.
        </Alert>
      )}

      {showRestoreBanner && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Automatic rollback</AlertTitle>
          The last update{status?.version ? ` (${status.version})` : ''} failed and the
          system was restored to the previous working version. Secrets and settings were
          left untouched. Check the log below before retrying.
          {status?.error ? <Box component="span" sx={{ display: 'block', mt: 1, fontFamily: 'monospace' }}>{status.error}</Box> : null}
        </Alert>
      )}

      {/* ------------------------------------------------ installed card --- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1">Installed versions</Typography>
          <Tooltip
            title={
              agentOnline
                ? `Host agent connected (last seen ${fmtDate(components?.hostAgent?.lastSeenAt)})`
                : 'Host agent offline — updates cannot start. Check cng-host-agent.service on the device.'
            }
          >
            <Chip
              size="small"
              icon={agentOnline ? <CloudDoneIcon /> : <CloudOffIcon />}
              color={agentOnline ? 'success' : 'error'}
              label={agentOnline ? 'Host agent online' : 'Host agent offline'}
              variant="outlined"
            />
          </Tooltip>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 220 }}>Application</TableCell>
                <TableCell>{components?.app || 'unknown'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Backend image</TableCell>
                <TableCell>
                  {comp.backend?.imageTag || '—'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Frontend image</TableCell>
                <TableCell>
                  {comp.frontend?.imageTag || '—'}
                  {uiVersion ? `  (UI build ${uiVersion})` : ''}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Host agent</TableCell>
                <TableCell>
                  {comp.hostAgent?.version || '—'}
                  {comp.hostAgent?.agentVersion ? ` (agent ${comp.hostAgent.agentVersion})` : ''}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Compose file</TableCell>
                <TableCell sx={{ borderBottom: 'none' }}>{comp.compose?.version || '—'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* -------------------------------------------------- start a run --- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Install a version
        </Typography>
        {versionsError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Could not load the version list from the registry: {versionsError}
          </Alert>
        )}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Version</InputLabel>
              <Select
                label="Version"
                value={selectedVersion}
                onChange={(e) => setSelectedVersion(e.target.value)}
                disabled={isActive || starting}
              >
                {versions.map((v) => (
                  <MenuItem key={v.version} value={v.version} disabled={v.isCurrent}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {v.version}
                      {v.isCurrent && <Chip size="small" label="current" />}
                      <Typography variant="caption" color="text.secondary">
                        {v.publishedAt ? new Date(v.publishedAt).toLocaleDateString() : ''}
                        {!v.components.frontend ? ' · backend only' : ''}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
                {versions.length === 0 && (
                  <MenuItem value="" disabled>
                    No published versions found
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeImages}
                  onChange={(e) => setIncludeImages(e.target.checked)}
                  disabled={isActive || starting}
                />
              }
              label="Update images"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeHostFiles}
                  onChange={(e) => setIncludeHostFiles(e.target.checked)}
                  disabled={isActive || starting}
                />
              }
              label="Host scripts"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              color={isRollback ? 'warning' : 'primary'}
              startIcon={isRollback ? <RestartAltIcon /> : <SystemUpdateAltIcon />}
              disabled={
                !selectedVersion ||
                isActive ||
                starting ||
                !agentOnline ||
                (!includeImages && !includeHostFiles)
              }
              onClick={() => setConfirmOpen(true)}
            >
              {starting ? 'Starting…' : isRollback ? 'Roll back' : 'Update'}
            </Button>
          </Grid>
        </Grid>
        {!agentOnline && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            The host agent is offline; updates cannot be started.
          </Typography>
        )}
        {currentVersion && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Currently installed: {currentVersion}
            {selectable.length === 0 && versions.length > 0 ? ' (up to date)' : ''}
          </Typography>
        )}
      </Paper>

      {/* ------------------------------------------------------ progress --- */}
      {(isActive || (status && wasActiveRef.current) || (status && ACTIVE_STATES.includes(status.state))) && status && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1">
              {status.mode === 'rollback' ? 'Rollback' : 'Update'} to {status.version}
              {status.state === 'restoring' && ' — restoring previous version'}
            </Typography>
            <Chip
              size="small"
              label={reconnecting ? 'reconnecting…' : status.state}
              color={
                reconnecting
                  ? 'warning'
                  : status.state === 'complete'
                  ? 'success'
                  : status.state === 'failed'
                  ? 'error'
                  : status.state === 'restoring'
                  ? 'warning'
                  : 'primary'
              }
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(Math.max(status.pct || 0, 0), 100)}
            color={status.state === 'restoring' || reconnecting ? 'warning' : 'primary'}
            sx={{ my: 2, height: 8, borderRadius: 4 }}
          />
          {reconnecting && (
            <Alert severity="info" sx={{ mb: 2 }}>
              The application is restarting as part of the update — reconnecting…
            </Alert>
          )}
          <List dense>
            {(status.steps || []).map((s) => (
              <ListItem key={s.key} disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <StepIcon status={s.status} />
                </ListItemIcon>
                <ListItemText
                  primary={s.label}
                  secondary={s.detail || null}
                  primaryTypographyProps={{
                    color: s.status === 'skipped' ? 'text.disabled' : 'text.primary',
                  }}
                />
              </ListItem>
            ))}
          </List>
          {(status.warnings || []).length > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {(status.warnings || []).map((w, i) => (
                <Box key={i} component="div">
                  {w}
                </Box>
              ))}
            </Alert>
          )}
          {status.error && !ACTIVE_STATES.includes(status.state) && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {status.error}
            </Alert>
          )}
        </Paper>
      )}

      {/* ------------------------------------------------------- history --- */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Update history
        </Typography>
        {history.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No updates have been run on this device yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>From → To</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell align="right">Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h, i) => (
                  <TableRow key={`${h.at}-${i}`}>
                    <TableCell>{fmtDate(h.at)}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{h.mode}</TableCell>
                    <TableCell>
                      {(h.from?.backend || '—').replace('backend-', '')} →{' '}
                      {(h.to?.backend || h.version || '—').replace('backend-', '')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={h.restored ? 'failed · auto-restored' : h.result}
                        color={
                          h.result === 'complete'
                            ? 'success'
                            : h.restored
                            ? 'warning'
                            : 'error'
                        }
                        variant="outlined"
                      />
                      {h.warnings > 0 && (
                        <Chip size="small" sx={{ ml: 0.5 }} label={`${h.warnings} warning${h.warnings > 1 ? 's' : ''}`} variant="outlined" color="warning" />
                      )}
                    </TableCell>
                    <TableCell>{h.initiatedBy || '—'}</TableCell>
                    <TableCell align="right">
                      {typeof h.durationSec === 'number' ? `${h.durationSec}s` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* -------------------------------------------------------- dialog --- */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {isRollback ? 'Roll back' : 'Update'} to {selectedVersion}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This will {includeImages ? 'pull and restart the application containers' : ''}
            {includeImages && includeHostFiles ? ' and ' : ''}
            {includeHostFiles ? 'update the host-side scripts' : ''} on this device.
            <Box sx={{ mt: 1 }}>
              The application will be briefly unavailable while containers restart. If the
              new version fails to become healthy, the device automatically restores{' '}
              {currentVersion || 'the current version'} with all settings and secrets intact.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={isRollback ? 'warning' : 'primary'}
            onClick={startRun}
          >
            {isRollback ? 'Roll back' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default SystemUpdate;
